import { useForm } from "react-hook-form";
import { Assistant, AssistantsService } from "@/generated";
import { useState } from "react";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";
import BaseDialog from "../Modals/BaseDialog";
import { usePermissions } from "@/hooks/usePermissions";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";
import { NoxusIconPickerField } from "@/components/Flows/NoxusIconPicker";
import { NoxusInputField } from "../Form/InputField";
import {
  MAX_ENTITY_NAME,
  MAX_ENTITY_DESCRIPTION,
} from "@/constants/entityLimits";

interface IAgentEditFormData {
  title: string;
  description: string;
  icon: string;
}

const AgentDetailsDialog = ({
  assistant,
  isOpen,
  onOpenChange,
}: {
  assistant: Assistant;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<IAgentEditFormData>({
    mode: "onChange",
    defaultValues: {
      title: assistant.name || "",
      description: assistant.description || "",
      icon: assistant.image_url || "coworker",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const { spotGroup } = useSpotUser();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group");

  const queryClient = useQueryClient();
  const editAssistant = useMutation({
    mutationFn: AssistantsService.updateAssistant,
    onSuccess: () => {
      SpotToast.success("Agent updated successfully");
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANT_ID] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKSHOP_RECENT] });
    },
    onError: () => {
      SpotToast.error("Error updating agent");
    },
    onSettled: () => {
      setIsLoading(false);
      onOpenChange(false);
    },
  });

  const handleFormSubmit = (data: IAgentEditFormData) => {
    setIsLoading(true);

    const body: Record<string, unknown> = {};

    if (data.title !== assistant.name) {
      body.name = data.title === "" ? "Untitled agent" : data.title;
    }

    if (data.description !== (assistant.description ?? "")) {
      body.description = data.description;
    }

    const currentIcon = assistant.image_url || "coworker";
    if (data.icon !== currentIcon) {
      body.image_url = data.icon;
    }

    editAssistant.mutate({
      path: {
        assistant_id: assistant.id,
        group_id: spotGroup.id,
      },
      body,
    });
  };

  const footerActions = [
    {
      label: "Cancel",
      props: { variant: "outline", onClick: () => onOpenChange(false) },
    },
    {
      label: "Save",
      props: {
        onClick: handleSubmit(handleFormSubmit),
        loading: isLoading,
        disabled: isLoading || !isDirty || !isValid,
      },
    },
  ];

  return (
    <BaseDialog
      title="Agent details"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      footerActions={footerActions}
    >
      <form
        className="w-full"
        id="agent-edit-form"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="flex flex-col gap-6 items-start w-full">
          <NoxusIconPickerField
            name="icon"
            label="Icon"
            control={control}
            size="md"
            disable={!canEdit}
          />
          <NoxusInputField
            name="title"
            maxLength={MAX_ENTITY_NAME}
            label="Title"
            errors={errors}
            forcedName={assistant.name || "Untitled"}
            disable={!canEdit}
            control={control}
            rules={{
              required: { value: true, message: "" },
            }}
            autoFocus
          />
          <NoxusInputField
            name="description"
            label="Description"
            maxLength={MAX_ENTITY_DESCRIPTION}
            isTextarea
            isResizable={false}
            helperText="Doesn’t affect the agent's behavior but helps other tools interpret it more accurately."
            disable={!canEdit}
            errors={errors}
            control={control}
          />
        </div>
      </form>
    </BaseDialog>
  );
};

export default AgentDetailsDialog;
